import { useCallback, useEffect, useState } from "react";
import { Copy, Trash2, Mail, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const EMPLOYMENT = { casual: "Casual", part_time: "Part-time", full_time: "Full-time" };

const copy = async (url) => {
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Invite link copied");
  } catch {
    window.prompt("Copy this secure invite link:", url);
  }
};

const InviteForm = ({ workplaces, onCreated }) => {
  const { membership } = useAuth();
  const [form, setForm] = useState({ email: "", role: "employee", employment_type: "casual", hourly_rate: "", workplace_ids: [] });
  const [busy, setBusy] = useState(false);
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  const toggleWp = (id) => set({ workplace_ids: form.workplace_ids.includes(id) ? form.workplace_ids.filter((x) => x !== id) : [...form.workplace_ids, id] });
  const submit = async (withEmail) => {
    if (withEmail && !form.email) { toast.error("Enter an email address"); return; }
    setBusy(true);
    try {
      const body = { ...form, email: withEmail ? form.email : null, hourly_rate: form.hourly_rate === "" ? null : Number(form.hourly_rate) };
      const { data } = await api.post("/invitations", body);
      if (withEmail) toast.success(data.email_sent ? `Invitation emailed to ${data.email}` : "Invitation created — email could not be sent, share the link instead");
      else copy(data.invite_url);
      set({ email: "", hourly_rate: "" });
      onCreated();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 p-5" data-testid="invite-form">
      <div className="space-y-1.5"><Label>Employee email</Label><Input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} placeholder="name@email.com" className="h-11 rounded-xl" data-testid="invite-email-input" /></div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5"><Label>Role</Label>
          <Select value={form.role} onValueChange={(v) => set({ role: v })}><SelectTrigger className="h-11 rounded-xl" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="employee">Employee</SelectItem>{membership?.role === "owner" && <SelectItem value="manager">Manager</SelectItem>}</SelectContent></Select>
        </div>
        <div className="space-y-1.5"><Label>Employment</Label>
          <Select value={form.employment_type} onValueChange={(v) => set({ employment_type: v })}><SelectTrigger className="h-11 rounded-xl" data-testid="invite-employment-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(EMPLOYMENT).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="space-y-1.5"><Label>Hourly rate (AUD)</Label><Input type="number" min="0" step="0.01" value={form.hourly_rate} onChange={(e) => set({ hourly_rate: e.target.value })} className="h-11 rounded-xl" data-testid="invite-rate-input" /></div>
      </div>
      {workplaces.length > 0 && (
        <div className="space-y-2"><Label>Workplaces</Label>
          <div className="flex flex-wrap gap-3">{workplaces.map((w) => (
            <label key={w.workplace_id} className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1.5 text-sm">
              <Checkbox checked={form.workplace_ids.includes(w.workplace_id)} onCheckedChange={() => toggleWp(w.workplace_id)} data-testid={`invite-workplace-${w.workplace_id}`} />{w.name}
            </label>))}
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => submit(true)} disabled={busy} className="rounded-full bg-[#00A85C] hover:bg-[#008F4F]" data-testid="invite-send-email-btn"><Mail className="mr-1.5 h-4 w-4" />Send email invite</Button>
        <Button onClick={() => submit(false)} disabled={busy} variant="outline" className="rounded-full" data-testid="invite-create-link-btn"><Link2 className="mr-1.5 h-4 w-4" />Create secure link</Button>
      </div>
    </div>
  );
};

const STATUS_CLS = { pending: "bg-amber-50 text-amber-800", accepted: "bg-emerald-50 text-emerald-700", revoked: "bg-slate-100 text-slate-500" };

const InviteList = ({ invites, onChange }) => {
  const revoke = async (id) => {
    try { await api.delete(`/invitations/${id}`); toast.success("Invitation revoked"); onChange(); } catch (err) { toast.error(formatError(err)); }
  };
  if (!invites.length) return <p className="text-sm text-slate-500" data-testid="invite-list-empty">No invitations yet.</p>;
  return (
    <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200" data-testid="invite-list">
      {invites.map((i) => (
        <li key={i.invitation_id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`invite-row-${i.invitation_id}`}>
          <div className="min-w-0"><p className="truncate text-sm font-medium">{i.email || "Secure invite link"}</p><p className="text-xs text-slate-500">{i.role} · {EMPLOYMENT[i.employment_type]}</p></div>
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_CLS[i.status]}`}>{i.status}</span>
            {i.status === "pending" && <>
              <Button size="icon" variant="ghost" onClick={() => copy(i.invite_url)} data-testid={`invite-copy-${i.invitation_id}`} aria-label="Copy link"><Copy className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => revoke(i.invitation_id)} data-testid={`invite-revoke-${i.invitation_id}`} aria-label="Revoke"><Trash2 className="h-4 w-4 text-red-600" /></Button>
            </>}
          </div>
        </li>
      ))}
    </ul>
  );
};

export const InvitePanel = () => {
  const [workplaces, setWorkplaces] = useState([]);
  const [invites, setInvites] = useState([]);
  const load = useCallback(async () => {
    const [w, i] = await Promise.all([api.get("/workplaces"), api.get("/invitations")]);
    setWorkplaces(w.data);
    setInvites(i.data);
  }, []);
  useEffect(() => {
    load().catch(() => {});
  }, [load]);
  return (
    <div className="space-y-5">
      <InviteForm workplaces={workplaces} onCreated={load} />
      <InviteList invites={invites} onChange={load} />
    </div>
  );
};
